import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useLocation } from 'react-router-dom'
import {
  LoadingSplashOverlay,
  makeSplashVariant,
  type SplashVariant,
} from '../components/loading-splash-overlay'

/**
 * Don't show the splash unless a load is *still* pending after this delay. Most
 * navigations / fetches resolve well under this, so the splash simply never
 * appears for them — it only surfaces for genuinely slow work. This is the fix
 * for "it shows on a timer even when nothing is really loading".
 */
const SHOW_DELAY_MS = 250
/** Once shown, stay up at least this long so a near-instant finish doesn't blink. */
const MIN_VISIBLE_MS = 320
/** Hard safety cap so a leaked load can never strand the splash on screen. */
const MAX_VISIBLE_MS = 8000
/** Matches the overlay's fade-out transition. */
const FADE_OUT_MS = 200

type ShowLoadingSplashOptions = {
  /** Skip starting a new load if the splash is already visible. */
  skipIfVisible?: boolean
}

type LoadingSplashContextValue = {
  /**
   * Mark the start of a genuine async load. The splash appears only if the load
   * is still running after a short delay. Returns an idempotent `end` callback —
   * call it (or let the returned promise settle via `runWithLoadingSplash`) when
   * the work completes.
   */
  showLoadingSplash: (options?: ShowLoadingSplashOptions) => () => void
  /** Force every pending load closed and hide the splash. */
  hideLoadingSplash: () => void
  /** Wrap an async unit of work so the splash reflects exactly its lifetime. */
  runWithLoadingSplash: <T>(work: Promise<T> | (() => Promise<T>)) => Promise<T>
  isLoadingSplashVisible: boolean
}

type RenderState = {
  variant: SplashVariant
  exiting: boolean
}

const LoadingSplashContext = createContext<LoadingSplashContextValue | null>(null)

/**
 * Collapse a pathname to its top-level module so route changes within one module
 * (e.g. announcement sub-channels) are not treated as a fresh navigation.
 */
function routeModule(pathname: string): string {
  if (pathname.startsWith('/announcements')) return '/announcements'
  return pathname
}

export function LoadingSplashProvider({ children }: { children: ReactNode }) {
  const location = useLocation()
  const [render, setRender] = useState<RenderState | null>(null)
  const renderRef = useRef<RenderState | null>(null)
  renderRef.current = render

  const pendingRef = useRef(0)
  const shownAtRef = useRef(0)
  const showTimerRef = useRef<number | null>(null)
  const hideTimerRef = useRef<number | null>(null)
  const exitTimerRef = useRef<number | null>(null)
  const maxTimerRef = useRef<number | null>(null)

  const clearTimer = (ref: { current: number | null }) => {
    if (ref.current !== null) {
      window.clearTimeout(ref.current)
      ref.current = null
    }
  }

  const present = useCallback(() => {
    clearTimer(showTimerRef)
    if (pendingRef.current === 0) return
    shownAtRef.current = Date.now()
    setRender({ variant: makeSplashVariant(), exiting: false })
    clearTimer(maxTimerRef)
    maxTimerRef.current = window.setTimeout(() => {
      pendingRef.current = 0
      clearTimer(hideTimerRef)
      setRender((r) => (r ? { ...r, exiting: true } : r))
      clearTimer(exitTimerRef)
      exitTimerRef.current = window.setTimeout(() => setRender(null), FADE_OUT_MS)
    }, MAX_VISIBLE_MS)
  }, [])

  const beginExit = useCallback(() => {
    clearTimer(hideTimerRef)
    if (!renderRef.current || renderRef.current.exiting) return
    setRender((r) => (r ? { ...r, exiting: true } : r))
    clearTimer(maxTimerRef)
    clearTimer(exitTimerRef)
    exitTimerRef.current = window.setTimeout(() => setRender(null), FADE_OUT_MS)
  }, [])

  const scheduleHide = useCallback(() => {
    // Was waiting to appear but the load finished first → never show it.
    if (showTimerRef.current !== null) {
      clearTimer(showTimerRef)
      return
    }
    if (!renderRef.current || renderRef.current.exiting) return
    const elapsed = Date.now() - shownAtRef.current
    const wait = Math.max(0, MIN_VISIBLE_MS - elapsed)
    clearTimer(hideTimerRef)
    hideTimerRef.current = window.setTimeout(beginExit, wait)
  }, [beginExit])

  const beginLoad = useCallback(
    (options?: ShowLoadingSplashOptions) => {
      if (options?.skipIfVisible && (renderRef.current || showTimerRef.current !== null)) {
        return () => {}
      }
      pendingRef.current += 1
      if (pendingRef.current === 1) {
        // A finished-but-still-fading splash being revived: cancel the exit.
        if (renderRef.current?.exiting) {
          clearTimer(exitTimerRef)
          clearTimer(hideTimerRef)
          shownAtRef.current = Date.now()
          setRender((r) => (r ? { ...r, exiting: false } : r))
        } else if (!renderRef.current && showTimerRef.current === null) {
          showTimerRef.current = window.setTimeout(present, SHOW_DELAY_MS)
        } else {
          clearTimer(hideTimerRef)
        }
      }

      let ended = false
      return () => {
        if (ended) return
        ended = true
        pendingRef.current = Math.max(0, pendingRef.current - 1)
        if (pendingRef.current === 0) scheduleHide()
      }
    },
    [present, scheduleHide],
  )

  const hideLoadingSplash = useCallback(() => {
    pendingRef.current = 0
    clearTimer(showTimerRef)
    beginExit()
  }, [beginExit])

  const runWithLoadingSplash = useCallback(
    <T,>(work: Promise<T> | (() => Promise<T>)): Promise<T> => {
      const end = beginLoad()
      const promise = typeof work === 'function' ? work() : work
      return Promise.resolve(promise).finally(end)
    },
    [beginLoad],
  )

  // Treat a top-level navigation as a candidate load: register it, then release
  // it once the destination has committed and the browser settles (≈ a couple of
  // frames). Instant renders release before SHOW_DELAY_MS, so the splash stays
  // hidden; only a genuinely slow/janky arrival keeps it pending long enough to
  // appear. This replaces the old fixed 1100ms "always show on switch" timer.
  const prevModuleRef = useRef(routeModule(location.pathname))
  const isInitialMountRef = useRef(true)

  useEffect(() => {
    const module = routeModule(location.pathname)
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false
      prevModuleRef.current = module
      return
    }
    if (prevModuleRef.current === module) return
    prevModuleRef.current = module

    const end = beginLoad({ skipIfVisible: true })
    let raf1 = 0
    let raf2 = 0
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(end)
    })
    return () => {
      cancelAnimationFrame(raf1)
      cancelAnimationFrame(raf2)
      end()
    }
  }, [location.pathname, beginLoad])

  useEffect(
    () => () => {
      clearTimer(showTimerRef)
      clearTimer(hideTimerRef)
      clearTimer(exitTimerRef)
      clearTimer(maxTimerRef)
    },
    [],
  )

  const value = useMemo<LoadingSplashContextValue>(
    () => ({
      showLoadingSplash: beginLoad,
      hideLoadingSplash,
      runWithLoadingSplash,
      isLoadingSplashVisible: render !== null && !render.exiting,
    }),
    [beginLoad, hideLoadingSplash, runWithLoadingSplash, render],
  )

  return (
    <LoadingSplashContext.Provider value={value}>
      {children}
      {render && <LoadingSplashOverlay variant={render.variant} exiting={render.exiting} />}
    </LoadingSplashContext.Provider>
  )
}

export function useLoadingSplash(): LoadingSplashContextValue {
  const ctx = useContext(LoadingSplashContext)
  if (!ctx) {
    throw new Error('useLoadingSplash must be used within LoadingSplashProvider')
  }
  return ctx
}
